// @flow

import React, { type Node, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Tooltip } from './tooltip';

type Size = 'half' | 'third' | 'quarter';

export type FieldProps = {
  showFloatingLabel?: boolean,
  label?: Node,
  children?: Node,
  hint?: Node,
  hasError?: boolean | null | typeof undefined,
  error?: Node,
  size?: Size,
  tooltip?: Node,
};

export function Field({
  showFloatingLabel,
  label,
  children,
  hasError,
  error,
  size,
  tooltip,
  ...rest
}: FieldProps): Node {
  const wrapperProps: any = { ...rest };

  if (hasError) {
    wrapperProps['data-error'] = true;
  }

  const className = useMemo(
    () =>
      buildClassName()
        .withHasError(hasError)
        .withSize(size)
        .withFloatingLabel(showFloatingLabel)
        .build(),
    [hasError, size, showFloatingLabel]
  );

  const ref = useRef();
  const setAttributeRef = useRef();

  const setAttribute = useCallback(
    (key, value) => {
      if (!setAttributeRef.current) {
        return;
      }

      if (key === 'class') {
        return;
      }

      setAttributeRef.current(key, value);
    },
    [setAttributeRef.current, className]
  );

  useLayoutEffect(() => {
    if (!ref.current || setAttributeRef.current) {
      return;
    }

    setAttributeRef.current = ref.current.setAttribute.bind(ref.current);
    ref.current.setAttribute = setAttribute;
  }, [ref.current]);

  useLayoutEffect(() => {
    if (!setAttributeRef.current) {
      return;
    }
    setAttributeRef.current('class', className);
  }, [setAttributeRef.current, className]);

  return (
    <div ref={ref} {...wrapperProps}>
      <div className={`field__input-wrapper ${tooltip ? 'field__input-wrapper--icon-right' : ''}`} style={{ position: 'relative' }}>
        {label}
        {children}
        {tooltip && <Tooltip>{tooltip}</Tooltip>}
      </div>
      {hasError && <p className="field__message field__message--error">{error}</p>}
    </div>
  );
}

class BuildClassName {
  hasError: ?boolean;
  size: ?Size;
  showFloatingLabel: ?boolean;

  withHasError(hasError: ?boolean): BuildClassName {
    this.hasError = hasError;
    return this;
  }

  withSize(size: ?Size): BuildClassName {
    this.size = size;
    return this;
  }

  withFloatingLabel(showFloatingLabel: ?boolean): BuildClassName {
    this.showFloatingLabel = showFloatingLabel;
    return this;
  }

  build(): string {
    const errorClass = this.hasError ? 'field--error' : '';
    const sizeClass = this.size ? `field--${this.size}` : '';
    const floatingLabelClass = this.showFloatingLabel ? 'field--show-floating-label' : '';
    return `field ${errorClass} ${sizeClass} ${floatingLabelClass} ${Date.now()}`;
  }
}

function buildClassName(): BuildClassName {
  return new BuildClassName();
}
